/**
 * Every `/integrations/<segment>` route that is **not** a catalog id.
 *
 * The generic connector detail page claims a dynamic segment, so it needs to know
 * what it must not answer for. These were the routes registered beside `:type` in
 * the Ember router, plus `all` for the Hydra-owned directory. Ember no longer
 * registers any `/integrations` route (ui#17066). Hydra serves each of these
 * segments from its own routes. `sql-import` has no route in either app, and
 * `warehouse`, `data_out`, `audiences` and `sql-sync` have routes only at deeper
 * paths, so their bare URLs have none. Adding a static `/integrations/<segment>`
 * route means adding it here, or `:type` claims it too.
 *
 * One list, two consumers — the ownership patterns in `route-ownership.ts` and the
 * `$type` route components — so the router's answer and the page's behaviour cannot
 * drift apart. TanStack resolves static segments ahead of `$type`, so only a listed
 * segment with no route for its bare URL reaches the `$type` components.
 */
export const NON_CATALOG_INTEGRATION_SEGMENTS: readonly string[] = [
  'all',
  'customerio',
  'mparticle_out',
  'segment_in',
  'segment_out',
  'cdp_in',
  'cdp_out',
  'data_out',
  'warehouse',
  'reporting-webhooks',
  'audiences',
  'facebook_lead_ads',
  'forms',
  'sql-import',
  'sql-sync',
];

/** Source text for the negative lookahead the ownership patterns embed. */
export const NON_CATALOG_INTEGRATION_SEGMENTS_PATTERN = NON_CATALOG_INTEGRATION_SEGMENTS.join('|');

/**
 * Whether a `:type` segment belongs to another route rather than to the catalog.
 *
 * A segment that does is left to whoever owns it: the `$type` route components
 * behave as though this page had not migrated, which hands the URL back to the
 * embedded app.
 */
export function isNonCatalogIntegrationSegment(segment: string): boolean {
  return NON_CATALOG_INTEGRATION_SEGMENTS.includes(segment.toLowerCase());
}
